import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import {
	seatStatus,
	areSeatsEqual,
	isCompanionSeat,
	isSofa,
	getSeatLayoutBoundingBox,
	hasUniqueSeatLabels
} from 'store/ticketingPublic';
import { clamp, dist } from 'utils/math';
import { accessNested, joinClasses } from 'utils/utils';
import { getCopy } from 'utils/copy';
import cursorClasses from 'components/shared/theaters/ticketing/ticketSeating/cursors.sss';
import s from 'components/shared/theaters/ticketing/ticketSeating/canvasSeatPicker.sss';

const MIN_CELL_SIZE_PX           = 60;
const CELL_RATIO                 = 0.75;
const MIN_FONT_SIZE              = 12;
const SEAT_LABEL_RATIO           = 0.4;
const SCREEN_LABEL_RATIO         = 0.8;
const SEAT_LABEL_MIN_SIZE_PX     = MIN_FONT_SIZE / SEAT_LABEL_RATIO;
const SCREEN_LABEL_MIN_HEIGHT_PX = MIN_FONT_SIZE / SCREEN_LABEL_RATIO;

/**
 * Return whether the user can click on the seat.
 *
 * @function
 * @param {Seat} seat - the seat to test
 * @return {boolean} true if the seat is clickable, false otherwise
 *
 */
const isSeatClickable = (seat) => (
	seat.status === seatStatus.AVAILABLE ||
	seat.status === seatStatus.RESERVED ||
	seat.status === seatStatus.WHEELCHAIR ||
	seat.status === seatStatus.COMPANION
);

/**
 * Get the label of a seat. Append a star to companion seat labels.
 *
 * @param {!Seat} seat - the input seat
 * @return {!string} the seat label
 */
const getSeatLabel = (seat) => {
	if (isCompanionSeat(seat) && !seat.seatLabel.endsWith('*')) {
		return seat.seatLabel + '*';
	}
	return seat.seatLabel;
};

const seatTypes = {
	SEAT: 1,
	SOFA: 2
};

const wheelchairIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" ' +
	'width="256" height="256"><path d="M46.836 9.127a8.586 8.586 0 0 0-8.588 8.586 8.586 8.586 0 0 ' +
	'0 8.588 8.588 8.586 8.586 0 0 0 8.586-8.588 8.586 8.586 0 0 0-8.586-8.586zm-.63 19.03a4 4 0 0 ' +
	'0-3.94 4.056v27.55a4 4 0 0 0 4 4h16.216l10.29 12.665a4 4 0 0 0 3.105 1.476H82c5.41.077 ' +
	'5.41-8.076 0-8h-4.219L67.491 57.24a4 4 0 0 0-3.104-1.476H50.266v-7.317h11.732c5.33.046 ' +
	'5.33-7.972 0-7.926H50.266v-8.308a4 4 0 0 0-4.06-4.057zM38.79 42.2c-11.356 2.139-20.008 ' +
	'12.153-20.008 24.11 0 13.502 11.032 24.535 24.535 24.535 10.477 0 19.462-6.644 ' +
	'22.965-15.928l-6.53-8.04a16.386 16.386 0 0 1-16.435 15.893 16.4 16.4 0 0 1-16.463-16.46 ' +
	'16.385 16.385 0 0 1 11.936-15.827v-8.283z" fill="#xxxxxx"/></svg>';

const colorizeSvg = (svgText, color) => {
	const img = new Image();
	svgText = svgText.replace(/#xxxxxx/g, color);
	img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
	return img;
};

export default class CanvasSeatPicker extends Component {

	constructor(props) {
		super(props);

		this.wasResized  = false;
		this.sizeRatio   = 1.0;
		this.canvasScale = window.devicePixelRatio;

		this.dragging = false;
		this.dragged  = false;

		this.preventNextClick = false;

		this.prevHoveredSeat = null;
		this.hoveredSeat     = null;

		this.userPos      = { x: 0, y: 0 };
		this.userPrevPos  = { x: 0, y: 0 };
		this.userDownPos  = { x: 0, y: 0 };
		this.userPrevDist = 1.0;
		this.userIsMouse  = props.client.deviceSize === 'desktop';

		this.lastTouchEnd = 0;

		this.pos = { x: 0.0, y: 0.0 }; // (0, 0) is the center of the canvas

		this.zoom       = 1.0;
		// this.zoomStep   = 0.75;
		this.zoomFactor = 1.6;
		this.zoomMin    = this.zoom;
		this.zoomMax    = this.zoom;

		this.$resetBtn = null;
		this.resetBtnVisible = false;

		this.dirty = false; // only redraw when the render is flagged as "dirty"

		this.cursorName = '';

		const vars = accessNested(props.appData, 'styling.variables', {});
		this.themeColor     = vars['ticketing-seatingThemeColor'];
		this.highlightColor = vars['ticketing-seatingHighlightColor'];
		this.bgColor        = vars['ticketing-mainBgColor'];

		this.copy = {
			screen: getCopy('SEATING_SCREEN').toUpperCase(),
			front:  getCopy('SEATING_FRONT').toUpperCase(),
			back:   getCopy('SEATING_BACK').toUpperCase()
		};

		this.onMouseMove     = this.onMouseMove.bind(this);
		this.onMouseDown     = this.onMouseDown.bind(this);
		this.onMouseUp       = this.onMouseUp.bind(this);
		this.onWheel         = this.onWheel.bind(this);
		this.onTouchMove     = this.onTouchMove.bind(this);
		this.onTouchStart    = this.onTouchStart.bind(this);
		this.onTouchEnd      = this.onTouchEnd.bind(this);
		this.onClick         = this.onClick.bind(this);
		this.onResize        = this.onResize.bind(this);
		this.redraw          = this.redraw.bind(this);
		this.draw            = this.draw.bind(this);
		this.zoomIn          = this.zoomIn.bind(this);
		this.zoomOut         = this.zoomOut.bind(this);
		this.resetZoomAndPos = this.resetZoomAndPos.bind(this);

		this.wheelchairBgImg = colorizeSvg(wheelchairIconSvg, this.bgColor);
		this.wheelchairThemeImg = colorizeSvg(wheelchairIconSvg, this.themeColor);
	}

	componentWillMount() {
		this.updateInternalLayout();
		this.updatePickedSeats();
	}

	componentDidMount() {
		this.onResize();
		this.updateCanvasRect();

		document.addEventListener('mousemove', this.onMouseMove);
		this.$canvas.addEventListener('mousedown', this.onMouseDown);
		document.addEventListener('mouseup', this.onMouseUp);

		this.$canvas.addEventListener('wheel', this.onWheel);
		this.$canvas.addEventListener('mousewheel', this.onWheel);

		document.addEventListener('touchmove', this.onTouchMove, { passive: false });
		this.$canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
		document.addEventListener('touchend', this.onTouchEnd);

		this.$canvas.addEventListener('click', this.onClick);

		this.ctx = this.$canvas.getContext('2d');

		this.dirty = true;

		// Kick off the rendering
		this.frame = requestAnimationFrame(this.draw);

		// Disable selection on the whole page
		document.body.classList.add(s.noSelect);

		this.setCursor('grab');
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.seatLayout !== this.props.seatLayout) {
			this.updateInternalLayout(nextProps.seatLayout);
			this.updateSizeRatio();
			this.updateZoomBounds();
		}
		if (nextProps.pickedSeats !== this.props.pickedSeats) {
			this.updatePickedSeats(nextProps.pickedSeats);
		}
	}

	shouldComponentUpdate() {
		return false;
	}

	componentWillUnmount() {
		document.removeEventListener('mousemove', this.onMouseMove);
		this.$canvas.removeEventListener('mousedown', this.onMouseDown);
		document.removeEventListener('mouseup', this.onMouseUp);

		this.$canvas.removeEventListener('wheel', this.onWheel);
		this.$canvas.removeEventListener('mousewheel', this.onWheel);

		document.removeEventListener('touchmove', this.onTouchMove);
		this.$canvas.removeEventListener('touchstart', this.onTouchStart);
		document.removeEventListener('touchend', this.onTouchEnd);

		this.$canvas.removeEventListener('click', this.onClick);

		this.ctx = null;

		cancelAnimationFrame(this.frame);

		// Re-enable selection
		document.body.classList.remove(s.noSelect);
	}

	/**
	 * Drag the seating map inside the canvas based on the current user position.
	 */
	drag() {
		if (this.props.controlsDisabled) return;

		this.pos.x += (this.userPos.x - this.userPrevPos.x);
		this.pos.y += (this.userPos.y - this.userPrevPos.y);

		this.userPrevPos.x = this.userPos.x;
		this.userPrevPos.y = this.userPos.y;

		this.dragged = true;
		this.dirty = true;

		this.showOrHideResetButton();
	}

	/**
	 * Start dragging the seating map inside the canvas.
	 *
	 * @param {number} x - initial X position
	 * @param {number} y - initial Y position
	 */
	startDrag(x, y) {
		if (this.props.controlsDisabled) return;

		this.userPrevPos.x = x;
		this.userPrevPos.y = y;

		this.userDownPos.x = x;
		this.userDownPos.y = y;

		this.dragging = true;

		this.dragged = false;

		this.setCursor('grabbing');
	}

	/**
	 * Stop dragging the seating map.
	 */
	endDrag() {
		if (this.props.controlsDisabled) return;
		this.dragging = false;
		this.setCursor(this.hoveredSeat ? 'pointer' : 'grab');
	}

	/**
	 * Perform a zoom in or a zoom out on the seat layout.
	 *
	 * @param {number} newZoom - the new zoom value
	 */
	doZoom(newZoom) {
		if (this.props.controlsDisabled) return;

		newZoom = clamp(newZoom, this.zoomMin, this.zoomMax);

		const zoomDiff = newZoom - this.zoom;
		if (zoomDiff === 0) return;

		if (this.canvasRect) {
			const x = this.userPos.x - this.canvasRect.left - this.canvasRect.width * 0.5 - this.pos.x;
			const y = this.userPos.y - this.canvasRect.top - this.canvasRect.height * 0.5 - this.pos.y;

			this.pos.x = this.pos.x - x / this.zoom * zoomDiff;
			this.pos.y = this.pos.y - y / this.zoom * zoomDiff;
		}

		this.zoom = newZoom;
		this.dirty = true;

		this.showOrHideResetButton();
	}

	/**
	 * Return whether the current zoom or position of the map is at the origin.
	 *
	 * @return {boolean} true if the map is at the origin, false otherwise
	 */
	isViewModified() {
		return Math.abs(this.zoom - 1.0) >= 0.01 ||
			Math.abs(this.pos.x) >= 10.0 || Math.abs(this.pos.y) >= 10.0;
	}

	/**
	 * Show or hide the reset button based on the current view state.
	 */
	showOrHideResetButton() {
		if (this.isViewModified()) {
			this.showResetButton();
		} else {
			this.hideResetButton();
		}
	}

	/**
	 * Show the reset button if needed.
	 */
	showResetButton() {
		if (!this.resetBtnVisible && this.$resetBtn !== null) {
			this.$resetBtn.classList.add(s.active);
			this.resetBtnVisible = true;
		}
	}

	/**
	 * Hide the reset button if needed.
	 */
	hideResetButton() {
		if (this.resetBtnVisible && this.$resetBtn !== null) {
			this.$resetBtn.classList.remove(s.active);
			this.resetBtnVisible = false;
		}
	}

	/**
	 * Reset the zoom and map position to the origin.
	 */
	resetZoomAndPos(event) {
		if (event) event.preventDefault();

		this.endDrag(); // Make sure dragging is disabled

		this.pos.x = 0.0;
		this.pos.y = 0.0;
		this.zoom = 1.0;

		this.dirty = true;

		this.hideResetButton();
	}

	onMouseMove(event) {
		this.userPos.x = event.clientX;
		this.userPos.y = event.clientY;

		if (this.dragging) this.drag();

		this.userIsMouse = true;
	}

	onMouseDown(event) {
		this.startDrag(event.clientX, event.clientY);
	}

	onMouseUp(event) {
		this.endDrag();

		const dx = Math.abs(event.clientX - this.userDownPos.x);
		const dy = Math.abs(event.clientY - this.userDownPos.y);

		// Prevent unwanted click events
		if (event.target === this.$canvas && dx > 5 && dy > 5) {
			this.preventNextClick = true;
		}
		// if (this.dragged && event.target === this.$canvas) {
		// 	this.preventNextClick = true;
		// }
	}

	onWheel(event) {
		const isZoomIn = event.deltaY ? event.deltaY < 0 : event.wheelDelta > 0;
		this.doZoom(isZoomIn ? this.zoom * this.zoomFactor : this.zoom / this.zoomFactor);
	}

	onTouchMove(event) {
		const { touches } = event;

		event.preventDefault();

		if (touches.length === 1) {
			this.userPos.x = touches[0].clientX;
			this.userPos.y = touches[0].clientY;
			if (this.dragging) this.drag();

		} else if (touches.length > 1) {
			const x1 = touches[0].clientX;
			const y1 = touches[0].clientY;
			const x2 = touches[1].clientX;
			const y2 = touches[1].clientY;

			this.userPos.x = (x1 + x2) / 2;
			this.userPos.y = (y1 + y2) / 2;
			if (this.dragging) this.drag();

			const userDist = dist(x1, y1, x2, y2);
			this.doZoom(this.zoom * (userDist / this.userPrevDist));
			this.userPrevDist = userDist;
		}
	}

	onTouchStart(event) {
		const { touches } = event;

		if (touches.length === 1) {
			this.startDrag(touches[0].clientX, touches[0].clientY);

		} else if (touches.length > 1) {
			event.preventDefault();

			const x1 = touches[0].clientX;
			const y1 = touches[0].clientY;
			const x2 = touches[1].clientX;
			const y2 = touches[1].clientY;

			const x = (x1 + x2) / 2;
			const y = (y1 + y2) / 2;
			this.startDrag(x, y);

			this.userPrevDist = dist(x1, y1, x2, y2);
		}
	}

	onTouchEnd(event) {
		if (event.timeStamp - this.lastTouchEnd <= 300) {
			event.preventDefault();
		}
		this.lastTouchEnd = event.timeStamp;

		this.endDrag();
	}

	onClick() {
		if (this.preventNextClick) {
			this.preventNextClick = false;
			return;
		}
		const hoveredSeat = this.getHoveredSeat();
		if (hoveredSeat) {
			Store.emit(actions.PICK_OR_UNPICK_SEATS, { seats: hoveredSeat.seat });
			this.dirty = true;
		}
	}

	onResize() {
		this.wasResized = true;
	}

	zoomIn(event) {
		if (event) event.preventDefault();

		if (this.canvasRect) {
			this.userPos.x = this.canvasRect.left + this.canvasRect.width / 2;
			this.userPos.y = this.canvasRect.top + this.canvasRect.height / 2;
		}

		this.doZoom(this.zoom * this.zoomFactor);

		this.userIsMouse = false;
	}

	zoomOut(event) {
		if (event) event.preventDefault();

		if (this.canvasRect) {
			this.userPos.x = this.canvasRect.left + this.canvasRect.width / 2;
			this.userPos.y = this.canvasRect.top + this.canvasRect.height / 2;
		}

		this.doZoom(this.zoom / this.zoomFactor);

		this.userIsMouse = false;
	}

	/**
	 * Update the internal seat layout used for rendering and picking.
	 *
	 * @param {SeatLayout=} seatLayout - the current seat layout
	 */
	updateInternalLayout(seatLayout) {
		if (!seatLayout) seatLayout = this.props.seatLayout;
		if (!seatLayout) {
			console.error('CanvasSeatPicker: seatLayout should be passed as a prop');
			return;
		}

		const { screenStart, screenWidth, areas } = seatLayout;
		const internalLayout = {};

		const uniqueLabels = hasUniqueSeatLabels(seatLayout);

		// Compute real boundaries
		const {
			left:   boundaryLeft,
			right:  boundaryRight,
			top:    boundaryTop,
			bottom: boundaryBottom
		} = getSeatLayoutBoundingBox(seatLayout);

		const distortion = this.getMeanSeatAspectRatio();
		const meanSeatSize = this.getMeanSeatSize(distortion); // but this is in the layout coords? not internal layout?
		const meanCellSize = meanSeatSize / CELL_RATIO;
		this.minCellSize = this.getMinCellSize(distortion);
		// ^^ Required to compute zoom bounds

		internalLayout.meanSeatSize = meanSeatSize;

		const heightWithoutScreen = (boundaryBottom - boundaryTop) * distortion;
		const screenHeight = meanCellSize * 1.5;

		const innerPadding = 2 * meanCellSize; // Leave 2 cells between the screen and the seats
		const padding = 2 * meanCellSize; // Add a padding of 2 cells around the whole map

		const innerHeight = heightWithoutScreen + screenHeight + 2 * innerPadding;
		const innerWidth = (boundaryRight - boundaryLeft) + 2 * innerPadding;

		const layoutWidth = innerWidth + 2 * padding;
		const layoutHeight = innerHeight + 2 * padding;

		internalLayout.width = layoutWidth;
		internalLayout.height = layoutHeight;

		// Clamp the screen width to the maximum area width
		const newScreenStart = Math.max(screenStart, boundaryLeft);
		const newScreenWidth = Math.min(screenStart + screenWidth, boundaryRight) - newScreenStart;

		internalLayout.screen = {
			x:      boundaryRight - (newScreenStart + newScreenWidth) + innerPadding + padding,
			y:      padding,
			width:  newScreenWidth,
			height: screenHeight
		};

		internalLayout.arrows = {
			xLeft:  padding + innerPadding * 0.5,
			xRight: layoutWidth - padding - innerPadding * 0.5,
			y:      padding + screenHeight + innerPadding + heightWithoutScreen * 0.5,
			length: 1.25 * meanCellSize
		};

		internalLayout.labels = {
			x:      layoutWidth * 0.5,
			yFront: padding + screenHeight + innerPadding * 0.5,
			yBack:  layoutHeight - padding - innerPadding * 0.5
		};

		// Compute the measures of each seat
		const internalSeats = [];
		internalLayout.seats = internalSeats;

		areas.forEach((area) => {
			const { width, height, columnCount, rowCount, rows } = area;
			let { left, top } = area;

			// Use the new referential:
			left = boundaryRight - left - width + innerPadding + padding;
			top = (boundaryBottom - top - height) * distortion + innerPadding + screenHeight + padding;

			const cellWidth  = width / columnCount;
			const cellHeight = height / rowCount * distortion;
			const cellSize   = Math.min(cellWidth, cellHeight) * CELL_RATIO;

			rows.forEach((row) => {
				const { rowLabel, seats } = row;

				for (let i = 0, il = seats.length; i < il; i++) {
					const seat = seats[i];
					const { columnIndex, rowIndex } = seat.position;

					const x = left + (columnCount - columnIndex - 0.5) * cellWidth;
					const y = top + (rowCount - rowIndex - 0.5) * cellHeight;

					const internalSeat = {
						y:          y,
						size:       cellSize,
						clickable:  isSeatClickable(seat),
						companion:  seat.status === seatStatus.COMPANION || seat.originalStatus === seatStatus.COMPANION,
						wheelchair: seat.status === seatStatus.WHEELCHAIR || seat.originalStatus === seatStatus.WHEELCHAIR
					};
					// NOTE: Here it is assumed that if a sofa is picked or sold, all of the seats
					//       composing the sofa are picked or sold (respectively).

					if (isSofa(seat, seatLayout)) {
						const { seatsInGroup } = seat;
						const seatCount = seatsInGroup.length;

						let maxColumnIndex = 0;
						for (const { columnIndex } of seatsInGroup) {
							maxColumnIndex = Math.max(columnIndex, maxColumnIndex);
						}
						const leftX = left + (columnCount - maxColumnIndex - 0.5) * cellWidth;

						const groupedSeats = seats.slice(i, i + seatCount);
						groupedSeats.sort((a, b) => a.columnIndex - b.columnIndex);

						let labels = groupedSeats.map(seat => getSeatLabel(seat)).reverse();
						if (!uniqueLabels) {
							labels = labels.map(l => rowLabel + l);
						}

						internalSeat.type   = seatTypes.SOFA;
						internalSeat.seat   = groupedSeats;
						internalSeat.leftX  = leftX;
						internalSeat.rightX = x;
						internalSeat.label  = labels;

						i += seatCount - 1; // Skip proccessed seats
					} else {
						internalSeat.type  = seatTypes.SEAT;
						internalSeat.seat  = seat;
						internalSeat.x     = x;
						internalSeat.label = getSeatLabel(seat);
						if (!uniqueLabels) {
							internalSeat.label = rowLabel + internalSeat.label;
						}
					}

					internalSeats.push(internalSeat);
				}
			});
		});

		this.internalLayout = internalLayout;
	}

	/**
	 * Get the mean aspect ratio of the current seat layout.
	 *
	 * @return {number} the mean aspect ratio of one seat in the layout
	 */
	getMeanSeatAspectRatio() {
		let accSeatAspectRatio = 0;
		let seatCount = 0;

		this.props.seatLayout.areas.forEach((area) => {
			const { width, height, columnCount, rowCount } = area;
			const seatAspectRatio = (width * rowCount) / (height * columnCount);

			area.rows.forEach((row) => row.seats.forEach(() => {
				accSeatAspectRatio += seatAspectRatio;
				seatCount += 1;
			}));
		});

		return accSeatAspectRatio / seatCount;
	}

	/**
	 * Get the mean seat size of the current seat layout.
	 *
	 * @param {number=} distortion - the distortion factor (height scale)
	 * @return {number} the mean size of one seat in the current layout
	 */
	getMeanSeatSize(distortion) {
		if (typeof distortion !== 'number') {
			distortion = 1.0;
		}

		let accSeatSize = 0;
		let seatCount = 0;

		this.props.seatLayout.areas.forEach((area) => {
			const { width, height, columnCount, rowCount } = area;
			const cellSize = Math.min(width / columnCount, height / rowCount * distortion);
			const seatSize = cellSize * CELL_RATIO;

			area.rows.forEach((row) => row.seats.forEach(() => {
				accSeatSize += seatSize;
				seatCount += 1;
			}));
		});

		return accSeatSize / seatCount;
	}

	/**
	 * Get the minimum cell size of the current seat layout.
	 *
	 * @param {number=} distortion - the distortion factor (height scale)
	 * @return {number} the minimum size of one cell in the current layout
	 */
	getMinCellSize(distortion) {
		if (typeof distortion !== 'number') {
			distortion = 1.0;
		}

		let minCellSize = Infinity;

		this.props.seatLayout.areas.forEach((area) => {
			const { width, height, columnCount, rowCount } = area;
			const cellSize = Math.min(width / columnCount, height / rowCount * distortion);
			minCellSize = Math.min(cellSize, minCellSize);
		});

		return minCellSize;
	}

	/**
	 * Update the size ratio from layout coords to pixels.
	 */
	updateSizeRatio() {
		const layoutWidth  = this.internalLayout.width;
		const layoutHeight = this.internalLayout.height;
		const layoutAspectRatio = layoutWidth / layoutHeight;

		const canvasWidth = this.$canvas.width / this.canvasScale;
		const canvasHeight = this.$canvas.height / this.canvasScale;
		const canvasAspectRatio = canvasWidth / canvasHeight;

		if (canvasAspectRatio < layoutAspectRatio) {
			this.sizeRatio = canvasWidth / layoutWidth;
		} else {
			this.sizeRatio = canvasHeight / layoutHeight;
		}
	}

	/**
	 * Update the internal seat layout with the given picked seats.
	 *
	 * @param {Array.<PickedSeat>} pickedSeats - the array of picked seats
	 */
	updatePickedSeats(pickedSeats) {
		if (!this.internalLayout) {
			console.error('CanvasSeatPicker: could not update picked seats, no internalLayout defined');
			return;
		}
		if (!pickedSeats) pickedSeats = this.props.pickedSeats;
		if (!pickedSeats) {
			console.error('CanvasSeatPicker: could no update picked seats, no pickedSeats defined');
			return;
		}

		this.internalLayout.seats.forEach((internalSeat) => {
			const { type, seat } = internalSeat;

			internalSeat.picked = !!pickedSeats.find((pickedSeat) => {
				if (type === seatTypes.SEAT) {
					return areSeatsEqual(pickedSeat.seat, seat);
				} else if (type === seatTypes.SOFA) {
					return !!seat.find((sofaSeat) => areSeatsEqual(pickedSeat.seat, sofaSeat));
				}
			});
		});
	}

	/**
	 * Update the zoom bounds based on the current seat layout and canvas dimensions.
	 */
	updateZoomBounds() {
		this.zoomMax = MIN_CELL_SIZE_PX / (this.minCellSize * this.sizeRatio);
		this.zoomMin = Math.min(this.zoomMax, 1.0);
		this.zoomTo = clamp(this.zoomTo, this.zoomMin, this.zoomMax);
	}

	/**
	 * Update the current canvas bounding rectangle used to compute exact mouse positions.
	 */
	updateCanvasRect() {
		if (this.$canvas) {
			this.canvasRect = this.$canvas.getBoundingClientRect();
		}
	}

	/**
	 * Change the currently displayed cursor
	 *
	 * @param {string} cursorName - the cursor name
	 */
	setCursor(cursorName) {
		if (cursorName === this.cursorName) return;

		const oldClass = cursorClasses[this.cursorName];
		const newClass = cursorClasses[cursorName];

		if (typeof oldClass === 'string') {
			this.$canvas.classList.remove(oldClass);
		}
		if (typeof newClass === 'string') {
			this.$canvas.classList.add(newClass);
		}

		this.cursorName = cursorName;
	}

	/**
	 * Return the seat at the given position, if any.
	 *
	 * @param {!number} x - position on the X axis of the canvas (in pixels)
	 * @param {!number} y - position on the Y axis of the canvas (in pixels)
	 * @return {InternalSeat} the seat (from the internal layout) at the given position or null
	 */
	getSeatAtPos(x, y) {
		if (x < 0 || x > this.canvasRect.width || y < 0 || y > this.canvasRect.height) return;

		const pctToPx = this.sizeRatio * this.zoom; // NOTE: Those are not physical pixels!
		const pxToPct = 1.0 / pctToPx;

		const layoutWidth = this.internalLayout.width;
		const layoutHeight = this.internalLayout.height;

		const layoutWidthPx = layoutWidth * pctToPx;
		const layoutHeightPx = layoutHeight * pctToPx;
		const layoutLeftPx = (this.canvasRect.width - layoutWidthPx) / 2 + this.pos.x;
		const layoutTopPx = (this.canvasRect.height - layoutHeightPx) / 2 + this.pos.y;

		const invCellRatio = 1.0 / CELL_RATIO;

		// Convert px to pct
		x = (x - layoutLeftPx) * pxToPct;
		y = (y - layoutTopPx) * pxToPct;

		for (const seat of this.internalLayout.seats) {
			const halfSize = 0.5 * seat.size * invCellRatio; // TODO: Refactor that
			if (y >= seat.y - halfSize && y <= seat.y + halfSize) {
				if (seat.type === seatTypes.SEAT) {
					if (x >= seat.x - halfSize && x <= seat.x + halfSize) {
						return seat;
					}
				} else if (seat.type === seatTypes.SOFA) {
					if (x >= seat.leftX - halfSize && x <= seat.rightX + halfSize) {
						return seat;
					}
				}
			}
		}

		// no seat found
		return null;
	}

	/**
	 * Return the seat hovered by the mouse.
	 *
	 * @return {InternalSeat} the seat (from the internal layout) currently under the mouse or null
	 */
	getHoveredSeat() {
		if (this.props.controlsDisabled) return null;
		if (!this.canvasRect) return null;

		const x = this.userPos.x - this.canvasRect.left;
		const y = this.userPos.y - this.canvasRect.top;

		const hoveredSeat = this.getSeatAtPos(x, y);
		if (hoveredSeat && hoveredSeat.clickable) {
			return hoveredSeat;
		}

		return null;
	}

	/**
	 * Force a redraw of the seating map.
	 */
	redraw() {
		this.dirty = true;
	}

	/**
	 * Draw the seating map based on the current state.
	 * Always call this function with requestAnimationFrame()
	 * (Should this function early out if drawing is not necessary?)
	 *
	 * @param {number} time - the current timestamp
	 */
	draw() {
		this.frame = requestAnimationFrame(this.draw);
		if (!this.ctx) return;

		if (this.wasResized) {
			if (this.props.client.isFirefox) {
				// Hack for Firefox, which seems to consider the instrisic height
				// as the concrete height in that specific case
				this.$canvas.width = 0;
				this.$canvas.height = 0;
			}

			this.updateCanvasRect();

			this.$canvas.removeAttribute('height');
			this.$canvas.style.height = 'auto';

			this.$canvas.style.width = this.$canvas.clientWidth + 'px';
			this.$canvas.style.height = this.$canvas.clientHeight + 'px';
			this.$canvas.width = this.$canvas.clientWidth * this.canvasScale;
			this.$canvas.height = this.$canvas.clientHeight * this.canvasScale;

			this.updateSizeRatio();
			this.updateZoomBounds();

			this.wasResized = false;
			this.dirty = true;
		}

		if (this.userIsMouse && !this.dragging) {
			this.hoveredSeat = this.getHoveredSeat();
		}

		if (this.prevHoveredSeat !== this.hoveredSeat) {
			this.setCursor(this.hoveredSeat ? 'pointer' : 'grab');
			this.prevHoveredSeat = this.hoveredSeat;
		}

		if (!this.dirty) return;
		this.dirty = false;

		const pctToPx = this.sizeRatio * this.zoom * this.canvasScale;

		const layoutWidth = this.internalLayout.width;
		const layoutHeight = this.internalLayout.height;

		const layoutWidthPx = layoutWidth * pctToPx;
		const layoutHeightPx = layoutHeight * pctToPx;
		const layoutLeftPx = (this.$canvas.width - layoutWidthPx) / 2 + this.pos.x * this.canvasScale;
		const layoutTopPx = (this.$canvas.height - layoutHeightPx) / 2 + this.pos.y * this.canvasScale;

		this.ctx.clearRect(0, 0, this.$canvas.width, this.$canvas.height);

		this.ctx.textAlign = 'center';
		this.ctx.textBaseline = 'middle';

		const { screen, arrows, labels, meanSeatSize } = this.internalLayout;

		// Screen
		this.drawScreen(
			layoutLeftPx + screen.x * pctToPx,
			layoutTopPx + screen.y * pctToPx,
			screen.width * pctToPx,
			screen.height * pctToPx
		);

		// Arrows
		this.drawScreenArrow(
			layoutLeftPx + arrows.xLeft * pctToPx,
			layoutTopPx + arrows.y * pctToPx,
			arrows.length * pctToPx
		);
		this.drawScreenArrow(
			layoutLeftPx + arrows.xRight * pctToPx,
			layoutTopPx + arrows.y * pctToPx,
			arrows.length * pctToPx
		);

		// Labels
		if (meanSeatSize * pctToPx * SEAT_LABEL_RATIO > 10 * this.canvasScale) {
			// ^ Prevents labels being shown when the map is very small
			this.ctx.font = (12 * this.canvasScale + 0.25 * pctToPx) + 'px sans-serif';
			this.ctx.fillStyle = this.themeColor;
			this.ctx.fillText(
				this.copy.front,
				layoutLeftPx + labels.x * pctToPx,
				layoutTopPx + labels.yFront * pctToPx
			);
			this.ctx.fillText(
				this.copy.back,
				layoutLeftPx + labels.x * pctToPx,
				layoutTopPx + labels.yBack * pctToPx
			);
		}

		const internalSeats = this.internalLayout.seats;

		for (let i = 0, il = internalSeats.length; i < il; i++) {
			const seat = internalSeats[i];
			const y = layoutTopPx + seat.y * pctToPx;

			if (seat.type === seatTypes.SEAT) {
				const x = layoutLeftPx + seat.x * pctToPx;

				this.drawSeat(seat, x, y, seat.size * pctToPx);

			} else if (seat.type === seatTypes.SOFA) {
				const leftX = layoutLeftPx + seat.leftX * pctToPx;
				const rightX = layoutLeftPx + seat.rightX * pctToPx;

				this.drawSofa(seat, leftX, rightX, y, seat.size * pctToPx);
			}
		}
	}

	/**
	 * Draw the theater screen based at the given position and scale.
	 *
	 * @param {!number} x - position of the screen on the X axis on the canvas (in pixels)
	 * @param {!number} y - position of the screen on the Y axis on the canvas (in pixels)
	 * @param {!number} width - width of the screen on the canvas (in pixels)
	 * @param {!number} height - height of the screen on the canvas (in pixels)
	 */
	drawScreen(x, y, width, height) {
		this.ctx.beginPath();
		this.ctx.fillStyle = this.themeColor;
		this.ctx.moveTo(x - height, y);
		this.ctx.lineTo(x + width + height, y);
		this.ctx.lineTo(x + width, y + height);
		this.ctx.lineTo(x, y + height);
		this.ctx.fill();

		if (height * 0.5 >= SCREEN_LABEL_MIN_HEIGHT_PX * this.canvasScale) {
			this.ctx.font = (10 * this.canvasScale + height * 0.1) + 'px sans-serif';
			this.ctx.fillStyle = this.bgColor;
			this.ctx.fillText(this.copy.screen, x + width / 2, y + height / 2);
		}
	}

	/**
	 * Draw an arrow that points upward (to the screen).
	 *
	 * @param {!number} x - the X position of the center of the arrow
	 * @param {!number} y - the Y position of the center of the arrow
	 * @param {!number} length - the length of the arrow
	 */
	drawScreenArrow(x, y, length) {
		// Arrow line
		const semiLength = 0.5 * length;

		this.ctx.beginPath();
		this.ctx.lineWidth = 1;
		this.ctx.strokeStyle = this.themeColor;
		this.ctx.moveTo(x, y + semiLength);
		this.ctx.lineTo(x, y - semiLength);
		this.ctx.stroke();

		// Arrow triangle
		const size = 0.25 * length;
		const semiBasis = 0.57735 * size; // = (1 / √3) × size

		this.ctx.beginPath();
		this.ctx.fillStyle = this.themeColor;
		this.ctx.moveTo(x - semiBasis, y - semiLength + size);
		this.ctx.lineTo(x, y - semiLength);
		this.ctx.lineTo(x + semiBasis, y - semiLength + size);
		this.ctx.fill();
	}

	/**
	 * Draw a seat on the canvas.
	 *
	 * @param {Seat} seat - the seat to draw
	 * @param {!number} x - position of the center of the seat on the X axis (in pixels)
	 * @param {!number} y - position of the center of the seat on the Y axis (in pixels)
	 * @param {!number} size - the size of one seat (in pixels)
	 */
	drawSeat(seat, x, y, size) {
		const shouldFill = seat.picked || !seat.clickable;
		const halfSize   = size / 2;
		const radius     = size / 3;

		this.ctx.beginPath();
		this.ctx.moveTo(x - halfSize, y - halfSize);
		this.ctx.lineTo(x + halfSize, y - halfSize);
		this.ctx.lineTo(x + halfSize, y + halfSize - radius);
		this.ctx.quadraticCurveTo(x + halfSize, y + halfSize, x + halfSize - radius, y + halfSize);
		this.ctx.lineTo(x - halfSize + radius, y + halfSize);
		this.ctx.quadraticCurveTo(x - halfSize, y + halfSize, x - halfSize, y + halfSize - radius);
		this.ctx.lineTo(x - halfSize, y - halfSize);

		if (seat.picked) {
			this.ctx.fillStyle = this.highlightColor;
			this.ctx.fill();
			this.ctx.strokeStyle = this.highlightColor;
			this.ctx.stroke();
		} else if (!seat.clickable) {
			this.ctx.globalAlpha = 0.333;
			this.ctx.fillStyle = this.themeColor;
			this.ctx.fill();
			this.ctx.globalAlpha = 1.0;
		} else {
			this.ctx.lineWidth = Math.min(size * 0.1, 1.0);
			this.ctx.strokeStyle = this.themeColor;
			this.ctx.stroke();
		}

		const textColor = shouldFill ? this.bgColor : this.themeColor;
		const label = seat.label || seat.seat.seatLabel;
		if (seat.wheelchair) {
			const wheelchairImg = shouldFill ? this.wheelchairBgImg : this.wheelchairThemeImg;
			if (size >= SEAT_LABEL_MIN_SIZE_PX * this.canvasScale) {
				const iconSize = size * 0.6;
				const iconX = x - iconSize / 2;
				const iconY = y - iconSize / 2 - size * 0.19;
				this.ctx.drawImage(wheelchairImg, iconX, iconY, iconSize, iconSize);

				this.ctx.font = (size * SEAT_LABEL_RATIO * 0.75) + 'px sans-serif';
				this.ctx.fillStyle = textColor;
				this.ctx.fillText(label, x, y + size * 0.28);
			} else {
				const iconSize = size * 0.75;
				const iconX = x - iconSize / 2;
				const iconY = y - iconSize / 2;
				this.ctx.drawImage(wheelchairImg, iconX, iconY, iconSize, iconSize);
			}
		} else if (size >= SEAT_LABEL_MIN_SIZE_PX * this.canvasScale) {
			this.ctx.font = (size * SEAT_LABEL_RATIO) + 'px sans-serif';
			this.ctx.fillStyle = textColor;
			this.ctx.fillText(label, x, y);
		}
	}

	/**
	 * Draw a sofa (grouped seat) on the canvas.
	 *
	 * @param {Sofa} sofa - the sofa to draw
	 * @param {!number} size - the size of one seat (in pixels)
	 * @param {!number} leftX - position of the leftmost seat of the sofa on the X axis (in pixels)
	 * @param {!number} rightX - position of the rightmost seat of the sofa on the X axis (in pixels)
	 * @param {!number} y - position of the center of the sofa on the Y axis (in pixels)
	 */
	drawSofa(sofa, leftX, rightX, y, size) {
		const shouldFill = sofa.picked || !sofa.clickable;
		const halfSize   = size / 2;
		const radius     = size / 3;
		const seatCount  = sofa.seat.length;

		const l = leftX - halfSize;
		const r = rightX + halfSize;
		const t = y - halfSize;
		const b = y + halfSize;

		this.ctx.beginPath();
		this.ctx.moveTo(l, t);
		this.ctx.lineTo(r, t);
		this.ctx.lineTo(r, b - radius);
		this.ctx.quadraticCurveTo(r, b, r - radius, b);
		this.ctx.lineTo(l + radius, b);
		this.ctx.quadraticCurveTo(l, b, l, b - radius);
		this.ctx.lineTo(l, t);

		if (sofa.picked) {
			this.ctx.fillStyle = this.highlightColor;
			this.ctx.fill();
			this.ctx.strokeStyle = this.highlightColor;
			this.ctx.stroke();
		} else if (!sofa.clickable) {
			this.ctx.globalAlpha = 0.333;
			this.ctx.fillStyle = this.themeColor;
			this.ctx.fill();
			this.ctx.globalAlpha = 1.0;
		} else {
			this.ctx.lineWidth = Math.min(size * 0.1, 1.0);
			this.ctx.strokeStyle = this.themeColor;
			this.ctx.stroke();
		}

		const textColor = shouldFill ? this.bgColor : this.themeColor;
		if (size >= SEAT_LABEL_MIN_SIZE_PX * this.canvasScale) {
			this.ctx.font = (size * SEAT_LABEL_RATIO) + 'px sans-serif';
			this.ctx.fillStyle = textColor;
			let x = leftX;
			const stepX = (rightX - leftX) / (seatCount - 1);
			for (let i = 0; i < seatCount; i++) {
				this.ctx.fillText(sofa.label[i] || sofa.seat[i].seatLabel, x, y);
				x += stepX;
			}
		}
	}

	render(props) {
		return (
			<div class={s.container}>
				<div class={s.btnBox}>
					<button class={joinClasses(s.btn, s.resetBtn)} onClick={this.resetZoomAndPos} title={getCopy('RESET_VIEW')} ref={$ref => this.$resetBtn = $ref}>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
							<path d="M49.383 15.678c-2.803.049-5.631.446-8.426 1.215C26.11 20.974 15.797 34.56 15.805 49.96h-9.98l16.568 19.703L38.96 49.961H27.807c-.009-10.07 6.653-18.838 16.33-21.498 9.712-2.67 19.93 1.497 25.025 10.223a22.283 22.283 0 0 1-3.365 26.916c-7.084 7.19-18.009 8.701-26.766 3.707l-5.945 10.423c13.43 7.66 30.404 5.308 41.258-5.709 10.854-11.017 12.982-28.029 5.181-41.388-5.85-10.02-16.236-16.17-27.351-16.89a33.936 33.936 0 0 0-2.791-.067z" />
						</svg>
					</button>
					<button class={s.btn} onClick={this.zoomIn} title={getCopy('ZOOM_IN')} ref={$ref => this.$zoomInBtn = $ref}>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
							<path d="M45 6C23.508 6 6 23.508 6 45s17.508 39 39 39c9.286 0 17.824-3.273 24.53-8.72l17.204 17.204a4 4 0 1 0 5.657-5.658l-17.193-17.19C80.694 62.91 84 54.332 84 45 84 23.508 66.492 6 45 6zm0 8c17.168 0 31 13.832 31 31S62.168 76 45 76 14 62.168 14 45s13.832-31 31-31z" />
							<path d="M44.94 26.943A4 4 0 0 0 41 31v10H31a4 4 0 1 0 0 8h10v10a4 4 0 1 0 8 0V49h10a4 4 0 1 0 0-8H49V31a4 4 0 0 0-4.06-4.057z" />
						</svg>
					</button>
					<button class={s.btn} onClick={this.zoomOut} title={getCopy('ZOOM_OUT')} ref={$ref => this.$zoomOutBtn = $ref}>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
							<path d="M45 6C23.508 6 6 23.508 6 45s17.508 39 39 39c9.286 0 17.824-3.273 24.53-8.72l17.204 17.204a4 4 0 1 0 5.657-5.658l-17.193-17.19C80.694 62.91 84 54.332 84 45 84 23.508 66.492 6 45 6zm0 8c17.168 0 31 13.832 31 31S62.168 76 45 76 14 62.168 14 45s13.832-31 31-31z" />
							<path d="M31 41a4 4 0 1 0 0 8h28a4 4 0 1 0 0-8H31z" />
						</svg>
					</button>
				</div>
				<canvas class={joinClasses(s.canvas, props.class)} ref={$ref => this.$canvas = $ref}></canvas>
			</div>
		);
	}

}
